const ORDER_KEY = 'pif_admin_sidebar_order_v1';
const PIN_KEY = 'pif_admin_sidebar_pinned_v1';
const THEME_KEY = 'pif_admin_theme_v1';

export const ADMIN_THEME_DEFAULTS = {
  primary: '#2563eb',
  bg: '#f8fafc',
  sidebarBg: '#1e293b',
  sidebarText: '#94a3b8',
  cardBg: '#ffffff',
  text: '#1e293b',
  muted: '#64748b',
  border: '#e2e8f0',
  toggleOn: '#f5a623',
  hideHeader: false,
};

export const ADMIN_THEME_PRESETS = [
  { id: 'default', label: 'Default', theme: { ...ADMIN_THEME_DEFAULTS } },
  {
    id: 'forest',
    label: 'Forest',
    theme: {
      ...ADMIN_THEME_DEFAULTS,
      primary: '#1a472a',
      sidebarBg: '#14281c',
      toggleOn: '#e0b85c',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    theme: {
      ...ADMIN_THEME_DEFAULTS,
      primary: '#334155',
      bg: '#f1f5f9',
      sidebarBg: '#0f172a',
      toggleOn: '#64748b',
    },
  },
  {
    id: 'amber',
    label: 'Amber',
    theme: {
      ...ADMIN_THEME_DEFAULTS,
      primary: '#d97706',
      sidebarBg: '#292524',
      toggleOn: '#f5a623',
    },
  },
];

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const scopedKey = (base, scope = 'admin') => (
  scope === 'partner' ? base.replace('_admin_', '_partner_') : base
);

export function loadSidebarOrder(scope = 'admin') {
  const raw = readJson(scopedKey(ORDER_KEY, scope), {});
  return {
    groups: raw.groups && typeof raw.groups === 'object' ? raw.groups : {},
    quickAccess: Array.isArray(raw.quickAccess) ? raw.quickAccess.map(String) : [],
  };
}

export function saveSidebarOrder(order, scope = 'admin') {
  try {
    localStorage.setItem(scopedKey(ORDER_KEY, scope), JSON.stringify({
      groups: order.groups || {},
      quickAccess: order.quickAccess || [],
    }));
  } catch {
    /* ignore */
  }
}

export function loadSidebarPinned(scope = 'admin') {
  try {
    const raw = localStorage.getItem(scopedKey(PIN_KEY, scope));
    if (raw == null) return true;
    return raw === '1';
  } catch {
    return true;
  }
}

export function saveSidebarPinned(pinned, scope = 'admin') {
  try {
    localStorage.setItem(scopedKey(PIN_KEY, scope), pinned ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function loadAdminTheme(scope = 'admin') {
  const raw = readJson(scopedKey(THEME_KEY, scope), {});
  return { ...ADMIN_THEME_DEFAULTS, ...raw };
}

export function saveAdminTheme(theme, scope = 'admin') {
  const next = { ...ADMIN_THEME_DEFAULTS, ...theme };
  try {
    localStorage.setItem(scopedKey(THEME_KEY, scope), JSON.stringify(next));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('pif-admin-theme', { detail: next }));
  return next;
}

export function applyAdminTheme(el, theme) {
  if (!el) return;
  const t = { ...ADMIN_THEME_DEFAULTS, ...theme };
  el.style.setProperty('--admin-primary', t.primary);
  el.style.setProperty('--admin-bg', t.bg);
  el.style.setProperty('--admin-sidebar-bg', t.sidebarBg);
  el.style.setProperty('--admin-sidebar-text', t.sidebarText);
  el.style.setProperty('--admin-card-bg', t.cardBg);
  el.style.setProperty('--admin-text-main', t.text);
  el.style.setProperty('--admin-text', t.text);
  el.style.setProperty('--admin-text-muted', t.muted);
  el.style.setProperty('--admin-border', t.border);
  el.style.setProperty('--admin-toggle-on', t.toggleOn);
  el.classList.toggle('is-page-header-hidden', t.hideHeader === true);
}

/** Keep saved ids, drop unknowns, append any new items at the end. */
export function mergeOrder(savedIds, items) {
  const ids = items.map((item) => item.id);
  const known = new Set(ids);
  const ordered = (savedIds || []).filter((id) => known.has(id));
  ids.forEach((id) => {
    if (!ordered.includes(id)) ordered.push(id);
  });
  return ordered;
}

export function reorderList(ids, fromId, toId) {
  const list = [...ids];
  const from = list.indexOf(fromId);
  const to = list.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return list;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list;
}
